import Account from '~/account/Account';
import { isMobileOrTabletDeviceByAgent } from '~/utils/agent';
import { LocalStorageHelper } from '~/utils/storage';
import { DWH_EVENTS } from '~/const';
import dwhExport from '~/api/dwhExport';
import { isEmptyObject, isInGameBrowser } from '~/utils/utils';
import store from '~/Store';
import { CategoryThemes } from '~/types/category';

export const isEnabledSoundsByCategory = (category: ICategory, thirdPartySoundMode = true) => {
    if (category?.videoBackground?.hasSound) {
        return true;
    }

    const hasMusic = !isEmptyObject(category?.audioTrack);
    if (!hasMusic) {
        return false;
    }

    return thirdPartySoundMode ? true : category.audioTrack.isFreeLicense;
};

export const isEnabledSoundsByBundle = (bundle: IBundle, thirdPartySoundMode = true) => {
    if (bundle?.videoBackground?.hasSound) {
        return true;
    }

    const hasMusic = !isEmptyObject(bundle?.audioTrack);
    if (!hasMusic) {
        return false;
    }

    return thirdPartySoundMode ? true : bundle.audioTrack.isFreeLicense;
};

export const isEnabledSoundsByBundleFullscreenGallery = (bundle: IBundle) => {
    if (bundle?.isFullscreenGallery && bundle.gallery?.find((s) => s.video?.hasSound)) {
        return true;
    }
    return false;
};

export const getSoundKeyForCurrentPage = (category: ICategory, bundle: IBundle) => {
    const { clientSettings } = store.getState().account;

    if (category?.theme === CategoryThemes.GROUPING_PAGE_THEME) {
        const { bundles } = store.getState().app;
        const hasSoundInCategory = !isEmptyObject(category?.audioTrack);
        const hasSoundInBundle = category.bundles.some((bundleId) => {
            return !isEmptyObject(bundles[bundleId]?.audioTrack);
        });
        return hasSoundInCategory || hasSoundInBundle ? CategoryThemes.GROUPING_PAGE_THEME : null;
    }

    if (isEnabledSoundsByBundle(bundle, clientSettings?.settings?.third_party_sounds_mode)) {
        return bundle.name;
    }

    if (isEnabledSoundsByCategory(category, clientSettings?.settings?.third_party_sounds_mode)) {
        return category.name;
    }

    if (isEnabledSoundsByBundleFullscreenGallery(bundle)) {
        return `gallery_${bundle.name}`;
    }

    return null;
};

export const isEnabledSoundsForCurrentPage = (category: ICategory, bundle: IBundle) => {
    return isEnabledSoundsByCategory(category) || isEnabledSoundsByBundle(bundle);
};

function isAvailableSoundForThirdParty(categoryName: string) {
    const { categories } = store.getState().app;
    const { clientSettings } = store.getState().account;

    if (!isInGameBrowser) {
        return true;
    }

    const category = categories?.[categoryName];
    if (!category) {
        return true;
    }

    if (category.audioTrack?.isFreeLicense) {
        return true;
    }

    if (clientSettings && !clientSettings.settings?.third_party_sounds_mode) {
        return false;
    }

    return true;
}

export const isAvailableSoundByKey = (soundKey: string, categoryName?: string) => {
    const lsKey = Account.getLSKey(`audio:${soundKey}`);
    if (categoryName) {
        if (LocalStorageHelper.get(lsKey) === null && !isAvailableSoundForThirdParty(categoryName)) {
            LocalStorageHelper.set(lsKey, false);
            return false;
        }
    }
    return LocalStorageHelper.get(lsKey) !== false;
};

export const updateSoundValueByKey = (soundKey: string, isEnabled: boolean) => {
    LocalStorageHelper.set(Account.getLSKey(`audio:${soundKey}`), isEnabled);
};

export const hasSoundSettingByCategory = (soundKey: string) => {
    if (isMobileOrTabletDeviceByAgent()) {
        return true;
    }

    return isAvailableSoundByKey(soundKey);
};

export const changeVolumeSoundByKey = (soundKey: string, isEnabled: boolean, isBundle = false) => {
    updateSoundValueByKey(soundKey, isEnabled);
    dwhExport.send(DWH_EVENTS.CHANGE_SOUND_STATUS, {
        [!isBundle ? 'category_name' : 'bundle_name']: soundKey,
        status: isEnabled ? 'on' : 'off',
    });
};

export const isEnabledSounds = (soundKey: string, categoryName: string) => {
    if (isMobileOrTabletDeviceByAgent()) {
        return false;
    }

    return isAvailableSoundByKey(soundKey, categoryName);
};

export const getDefaultValueBySoundKey = (soundKey: string, isTrusted: boolean, categoryName: string) => {
    if (!soundKey || (!isInGameBrowser && !isTrusted)) {
        return false;
    }

    return isEnabledSounds(soundKey, categoryName);
};
